import { Injectable, inject, signal } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client/core';
import { Observable, catchError, map, of } from 'rxjs';
import { Order } from 'src/app/order-module/shared/entities/order/order';
import { Contact } from 'src/app/rent-module/shared/entities/contact/contact';
import { Resource } from 'src/app/rent-module/shared/entities/resource/resource';
import { Task } from 'src/app/rent-module/shared/entities/task/task';
import * as fromGraphQl from './graphql';

export interface SearchResults {
  orders: Order[];
  resources: Resource[];
  customers: Contact[];
  issues: fromGraphQl.Issue[];
  tasks: Task[];
  noOrderFound: boolean;
  noResourceFound: boolean;
  noCustomerFound: boolean;
  noIssueFound: boolean;
  noTaskFound: boolean;
}

const SEARCH_HISTORY_KEY = 'lastSearchTerms';
const MAX_SEARCH_HISTORY = 5;

@Injectable({
  providedIn: 'root'
})
export class OrdersSearchService {
  private readonly apollo = inject(Apollo);
  
  readonly searchHistory = signal<string[]>(this.loadSearchHistory());

  search(searchTerm: string): Observable<SearchResults> {
    if (!searchTerm || searchTerm.length < 1) {
      return of(this.getEmptyResults());
    }

    return this.apollo.query<fromGraphQl.SearchQueryResponse>({
      query: fromGraphQl.SEARCH_QUERY,
      variables: {
        search: searchTerm,
        number: 0,
        size: 5,
        sortPropertyOrder: 'number',
        sortPropertyResource: 'name',
        sortPropertyContact: 'id',
        sortPropertyIssue: 'number',
        sortPropertyTask: 'id',
        sortDirection: 'asc',
      },
      errorPolicy: 'all'
    }).pipe(
      map((result: ApolloQueryResult<fromGraphQl.SearchQueryResponse>) => {
        this.addSearchTerm(searchTerm);
        
        const orders = result?.data?.orders?.content?.length > 0
          ? Order.createOrders(result.data.orders.content)
          : [];
          
        const resources = result?.data?.resources?.content?.length > 0
          ? Resource.createResources(result.data.resources.content)
          : [];
          
        const customers = result?.data?.customers?.content?.length > 0
          ? Contact.createContacts(result.data.customers.content)
          : [];

        const issues = result?.data?.issues?.content?.length > 0
          ? result.data.issues.content
          : [];

        const tasks = result?.data?.tasks?.content?.length > 0
          ? Task.createTasks(result.data.tasks.content)
          : [];

        return {
          orders,
          resources,
          customers,
          issues,
          tasks,
          noOrderFound: orders.length === 0,
          noResourceFound: resources.length === 0,
          noCustomerFound: customers.length === 0,
          noIssueFound: issues.length === 0,
          noTaskFound: tasks.length === 0
        };
      }),
      catchError(error => {
        console.error('Search error:', error);
        return of(this.getEmptyResults());
      })
    );
  }

  private getEmptyResults(): SearchResults {
    return {
      orders: [],
      resources: [],
      customers: [],
      issues: [],
      tasks: [],
      noOrderFound: false,
      noResourceFound: false,
      noCustomerFound: false,
      noIssueFound: false,
      noTaskFound: false
    };
  }

  private addSearchTerm(term: string): void {
    const currentHistory = this.searchHistory();
    
    // Remove duplicate if exists
    const filteredHistory = currentHistory.filter(t => t !== term);
    
    // Add to beginning
    const newHistory = [term, ...filteredHistory].slice(0, MAX_SEARCH_HISTORY);
    
    this.searchHistory.set(newHistory);
    this.saveSearchHistory(newHistory);
  }

  private loadSearchHistory(): string[] {
    try {
      const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private saveSearchHistory(history: string[]): void {
    try {
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
      console.error('Failed to save search history:', error);
    }
  }
}
